import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayerDataContext } from '../../App';
import BoardModeTabs from '../UI/BoardModeTabs';
import styles from './Play3DClient.module.css';

const firstText = (...values) => values.find(value => typeof value === 'string' && value.trim())?.trim() || '';

export default function Play3DClient() {
  const navigate = useNavigate();
  const playerDataContext = useContext(PlayerDataContext);
  const playerData = useMemo(() => playerDataContext || {}, [playerDataContext]);
  const frameRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const gameUrl = useMemo(() => {
    const params = new URLSearchParams();
    const playerName = firstText(
      playerData.playerName,
      playerData.name,
      playerData.username,
      playerData.player?.name,
      localStorage.getItem('playerName'),
      'Player 1'
    );
    const opponentName = firstText(
      playerData.opponentName,
      playerData.opponent?.name,
      playerData.players?.find?.(player => player?.playerId !== playerData.playerId)?.name,
      'Player 2'
    );

    params.set('embedded', '1');
    params.set('reload', String(reloadKey));
    params.set('playerName', playerName);
    params.set('opponentName', opponentName);
    if (playerData.playerId) params.set('playerId', String(playerData.playerId));
    if (playerData.roomId) params.set('roomId', String(playerData.roomId));
    if (playerData.color) params.set('color', String(playerData.color));

    return `/play3d/index.html?${params.toString()}`;
  }, [playerData, reloadKey]);

  useEffect(() => {
    setLoaded(false);
    setLoadFailed(false);
    const failureTimer = window.setTimeout(() => setLoadFailed(true), 20000);
    return () => window.clearTimeout(failureTimer);
  }, [gameUrl]);

  const reloadGame = () => {
    setLoaded(false);
    setLoadFailed(false);
    setReloadKey(value => value + 1);
  };


  const switchToNormalBoard = () => {
    // In-app navigation preserves the active socket and player session. A direct
    // /play3d refresh has no socket, so reload /play once to restore localStorage.
    if (playerData?.roomId) navigate('/play');
    else window.location.assign('/play');
  };

  const requestFullscreen = async () => {
    try {
      await frameRef.current?.requestFullscreen?.();
    } catch (error) {
      console.warn('Unable to enter fullscreen:', error);
    }
  };

  return (
    <main className={styles.shell} aria-label="Ludo City 3D game client">
      {!loaded && (
        <div className={styles.loading} role="status">
          <div className={styles.spinner} />
          <strong>Loading Ludo City 3D…</strong>
          <span>Preparing the board, humans and center dice.</span>
          {loadFailed && (
            <button type="button" onClick={reloadGame}>Retry 3D client</button>
          )}
        </div>
      )}

      <div className={styles.controls}>
        <BoardModeTabs
          activeMode="3d"
          onSelect2D={switchToNormalBoard}
          onSelect3D={() => frameRef.current?.contentWindow?.focus?.()}
        />
        <button type="button" onClick={requestFullscreen} title="Fullscreen">⛶</button>
        <button type="button" onClick={reloadGame} title="Reload 3D board">↻</button>
      </div>

      <iframe
        key={gameUrl}
        ref={frameRef}
        className={styles.frame}
        src={gameUrl}
        title="Ludo City 3D"
        allow="autoplay; fullscreen; gamepad; clipboard-read; clipboard-write"
        onLoad={() => {
          setLoaded(true);
          setLoadFailed(false);
          frameRef.current?.contentWindow?.focus?.();
        }}
      />
    </main>
  );
}
